// DispatchDeck — day-level forgotten-clock-out flags for payroll review (TASK-10040)
//
// PURE module: no Firebase, no DOM, no AppState. Loaded in the browser via ONE
// side-effect import from clock.js (`import '../shiftDayFlags.js?v=…'`) which
// sets window.ShiftDayFlags — every other module reads window.ShiftDayFlags at
// call time (Pitfall #1: never a second `?v=` import). Node unit tests
// require() it directly (UMD): `node tests/task10040-missed-clockout.unit.cjs`.
//
// WHY THIS EXISTS. Willy (TITS) forgot to clock out of Shop 10/15 4:26 PM and
// closed it 10/16 3:05 PM: 22.65 h. That is under the 24 h clock-out text and
// under TITS's 24 h "Flag Single Shift Over", so nothing said a word. Two
// things gave it away that no flag looked at (Chris-approved 10/23):
//   1. The weekly grid books a whole shift on its clock-IN day, so his Thursday
//      read 30.7 h — more hours than the day has. Any single DAY over 24 h.
//   2. Shop / no-job time that runs past midnight. Real overnight work is on a
//      job; nobody sweeps the shop floor through the night.
// DISPLAY ONLY: these mark a row for review. They never cap or change hours
// and never send a text.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.ShiftDayFlags = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // More hours than a calendar day holds can only be a forgotten clock-out (or
  // two overlapping shifts) — the grid books each shift on its clock-in day.
  const DAY_LIMIT_HOURS = 24;

  // Same legacy forms internalTime.js treats as Shop: a null customer named
  // "Shop", or the literal sentinel the old jobboard repoint stored.
  const LEGACY_SHOP_ID = '__shop__';

  function _ms(v) {
    if (v == null || v === '') return NaN;
    if (typeof v === 'number') return v;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    return new Date(v).getTime();
  }

  // Shop or no job: no job on the entry (Shop / Office time is a customer with
  // no job — confirmed on TITS 10/23), or the legacy Shop forms.
  function isOffJob(entry) {
    if (!entry) return false;
    if (!String(entry.jobId == null ? '' : entry.jobId).trim()) return true;
    if (entry.customerId === LEGACY_SHOP_ID) return true;
    return String(entry.customerName || '').trim().toLowerCase() === 'shop';
  }

  // Does a [inMs, outMs) span touch two calendar days? `dayKeyOf(ms)` returns
  // the company 'YYYY-MM-DD' for an instant. A shift ending at exactly
  // midnight stays on its day (the last millisecond counted is outMs - 1).
  function crossesMidnight(inMs, outMs, dayKeyOf) {
    if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || outMs <= inMs) return false;
    if (typeof dayKeyOf !== 'function') return false;
    const a = dayKeyOf(inMs);
    const b = dayKeyOf(outMs - 1);
    return !!a && !!b && a !== b;
  }

  // A COMPLETED Shop / no-job shift that ran past midnight. Open shifts are the
  // clock-out text's job (and Payroll Prep already refuses to export them).
  function entryCrossesMidnightOffJob(entry, dayKeyOf) {
    if (!entry || entry.status !== 'completed' || !isOffJob(entry)) return false;
    return crossesMidnight(_ms(entry.clockIn), _ms(entry.clockOut), dayKeyOf);
  }

  // Days over the limit from a { dayKey: hours } map → [{ key, hours }] sorted
  // by key. Keys are whatever the caller buckets by (a 'YYYY-MM-DD' or a
  // weekday index); `skip(key)` drops a day the office already reviewed.
  function daysOverLimit(dayHours, limit, skip) {
    const lim = Number.isFinite(limit) ? limit : DAY_LIMIT_HOURS;
    const out = [];
    Object.keys(dayHours || {}).forEach((k) => {
      const h = Number(dayHours[k]) || 0;
      if (h > lim + 1e-9 && !(typeof skip === 'function' && skip(k))) out.push({ key: k, hours: h });
    });
    return out.sort((a, b) => (String(a.key) < String(b.key) ? -1 : String(a.key) > String(b.key) ? 1 : 0));
  }

  // Has the office already dealt with this over-24 h day? Only when it
  // accepted at least one shift on it (Payroll Prep Accept — the only Accept
  // there is, and only on flagged shifts) AND the shifts it has NOT accepted
  // add up to a normal day. So accepting Willy's forgotten Shop shift clears his
  // Thursday, but a later unaccepted shift that pushes the rest over 24 h
  // flags the day again. ({ accepted, openHours } per day; missing → not reviewed.)
  function dayReviewed(d, limit) {
    const lim = Number.isFinite(limit) ? limit : DAY_LIMIT_HOURS;
    return !!(d && d.accepted && (Number(d.openHours) || 0) <= lim + 1e-9);
  }

  return { DAY_LIMIT_HOURS, isOffJob, crossesMidnight, entryCrossesMidnightOffJob, daysOverLimit, dayReviewed };
});
